import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Check, Clock, AlertCircle, Send, Calendar } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface ClientHomeworkViewProps {
  clientId: string;
  clinicianId: string;
}

export function ClientHomeworkView({ clientId, clinicianId }: ClientHomeworkViewProps) {
  const queryClient = useQueryClient();
  const [reflectionId, setReflectionId] = useState<string | null>(null);
  const [reflection, setReflection] = useState("");

  const { data: homework, isLoading } = useQuery({
    queryKey: ['client-homework-view', clientId, clinicianId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_homework')
        .select('*')
        .eq('client_id', clientId)
        .eq('clinician_id', clinicianId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const completeMutation = useMutation({
    mutationFn: async (homeworkId: string) => {
      const { error } = await supabase
        .from('client_homework')
        .update({ 
          status: 'completed', 
          completed_at: new Date().toISOString() 
        })
        .eq('id', homeworkId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-homework-view'] });
      toast.success('Homework marked as complete!');
    }
  });

  const reflectionMutation = useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      const { error } = await supabase
        .from('client_homework')
        .update({ client_reflection: text })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-homework-view'] });
      setReflectionId(null);
      setReflection("");
      toast.success('Reflection saved');
    }
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <Check className="h-4 w-4 text-green-500" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-yellow-500" />;
      default: return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/10 text-green-500">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/10 text-yellow-500">In Progress</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const pendingHomework = homework?.filter(h => h.status !== 'completed') || [];
  const completedHomework = homework?.filter(h => h.status === 'completed') || [];

  if (isLoading) {
    return (
      <Card className="bg-card/50 border-border/30">
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Homework */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            My Homework
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingHomework.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No pending homework assignments</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingHomework.map(item => (
                <div key={item.id} className="p-4 rounded-lg bg-muted/30 border border-border/30">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status || 'pending')}
                      <h4 className="font-medium text-foreground">{item.title}</h4>
                    </div>
                    {getStatusBadge(item.status || 'pending')}
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
                  
                  {item.due_date && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                      <Calendar className="h-3 w-3" />
                      Due: {format(new Date(item.due_date), 'MMM d, yyyy')}
                    </div>
                  )}

                  {reflectionId === item.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={reflection}
                        onChange={(e) => setReflection(e.target.value)}
                        placeholder="Share your thoughts or reflections..."
                        className="bg-background"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => reflectionMutation.mutate({ id: item.id, text: reflection })}
                          disabled={!reflection.trim()}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setReflectionId(null); setReflection(""); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      {item.status !== 'completed' && (
                        <Button
                          size="sm"
                          onClick={() => completeMutation.mutate(item.id)}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Mark Complete
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setReflectionId(item.id); setReflection(item.client_reflection || ""); }}
                      >
                        Add Reflection
                      </Button>
                    </div>
                  )}

                  {item.clinician_feedback && (
                    <div className="mt-3 p-2 rounded bg-primary/10 border border-primary/20">
                      <p className="text-xs font-medium text-primary mb-1">Therapist Feedback:</p>
                      <p className="text-sm text-foreground">{item.clinician_feedback}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Homework */}
      {completedHomework.length > 0 && (
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              Completed ({completedHomework.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {completedHomework.slice(0, 5).map(item => (
                <div key={item.id} className="p-3 rounded-lg bg-muted/20 border border-border/20">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-foreground">{item.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {item.completed_at && format(new Date(item.completed_at), 'MMM d')}
                    </span>
                  </div>
                  {item.client_reflection && (
                    <p className="text-sm text-muted-foreground mt-1 italic">
                      "{item.client_reflection}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
